import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Badge, Card, CardContent, EmptyState, PageLoading, buttonVariants, cn } from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { listMtmAssessments } from "../../lib/api-client";

export function PatientReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;

  const { data: assessments, isLoading } = useQuery({
    queryKey: ["patient-mtm", patientId],
    queryFn: () => listMtmAssessments(patientId),
  });

  if (isLoading) return <PageLoading />;

  const completed = assessments?.filter((a) => a.status === "COMPLETED") ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.reports.title")}</h1>

      <Card>
        <CardContent className="p-0">
          {completed.length === 0 ? (
            <EmptyState className="border-0" title={t("patient.reports.empty")} icon={<FileText className="h-8 w-8" />} />
          ) : (
            <ul className="divide-y divide-charcoal-100">
              {completed.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-4">
                  <div>
                    <p className="font-medium text-charcoal-800">{a.type.replaceAll("_", " ")}</p>
                    <p className="text-sm text-charcoal-400">
                      {t("patient.reports.generatedOn", { date: a.completedAt ? new Date(a.completedAt).toLocaleDateString() : "" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="success">{a.status}</Badge>
                    <Link to={`/patient/reports/${a.id}`} className={cn(buttonVariants({ variant: "outline", size: "sm" }))}>
                      {t("patient.reports.view")}
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
